import { defineComponent, onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { Icon } from '@iconify/vue'
import { NInput, NModal, useMessage } from 'naive-ui'
import UserAvatar from '@/components/user-avatar'
import { showToast, showSuccessToast } from 'vant'
import { useAuthStore } from '@/stores/auth-store'
import { useUserStore } from '@/stores/user-store'
import { useCountdown } from '@/composables/use-countdown'
import { useServerCaptcha } from '@/composables/use-server-captcha'
import {
  postApiV1SendCode,
  postApiV1LoginRegister,
  postApiV1Login,
  getApiV1UserByIdProfile,
} from '@/api/sdk.gen'

// ==================== 49208 图标 ====================

import iconAf18 from '@/assets/mine-icons/af18_my-home.png'
import iconB004 from '@/assets/mine-icons/b004_my-collections.png'
import iconE98e from '@/assets/mine-icons/e98e_my-comments.png'
import iconFf5a from '@/assets/mine-icons/ff5a_my-follows.png'
import icon2901 from '@/assets/mine-icons/2901_my-fans.png'
import icon6bf3 from '@/assets/mine-icons/6bf3_my-likes.png'
import iconBlacklist from '@/assets/mine-icons/350f_blacklist.png'
import iconFansRank from '@/assets/mine-icons/be25_fans-rank.png'
import iconSettings from '@/assets/mine-icons/4fe1_mineset.png'
import iconDownloadApp from '@/assets/mine-icons/8c05_download-app.png'
import iconSiteMsg from '@/assets/mine-icons/sitemsg.png'
import iconContactService from '@/assets/mine-icons/contact-service.png'
import iconClearCache from '@/assets/mine-icons/clear-cache.png'

// ==================== 菜单数据 ====================

interface MenuItem {
  text: string
  icon: string
  to?: string
  authRequired?: boolean
  action?: 'userHome' | 'logout' | 'clearCache'
  // 套壳 APP 内走 Custom Tabs（带 × 关闭按钮），其他环境维持 router.push
  nativeOpen?: boolean
}

const channel: MenuItem[] = [
  { text: '我的主页', icon: iconAf18, action: 'userHome', authRequired: true },
  { text: '我的收藏', icon: iconB004, to: '/my-collections', authRequired: true },
  { text: '我的评论', icon: iconE98e, to: '/my-comments', authRequired: true },
  { text: '我的关注', icon: iconFf5a, to: '/my-follows', authRequired: true },
  { text: '我的粉丝', icon: icon2901, to: '/my-fans', authRequired: true },
  { text: '我的点赞', icon: icon6bf3, to: '/my-likes', authRequired: true },
  { text: '黑名单', icon: iconBlacklist, to: '/blacklist', authRequired: true },
]

const top: MenuItem[] = [{ text: '粉丝排行榜', icon: iconFansRank, to: '/top-fans-rank' }]

const others: MenuItem[] = [
  { text: '站内信息', icon: iconSiteMsg, to: '/sitemsg' },
  { text: '联系客服', icon: iconContactService, to: '/contact-customer-service' },
  { text: '清除缓存', icon: iconClearCache, action: 'clearCache' },
  { text: '下载APP', icon: iconDownloadApp, to: '/download-app', nativeOpen: true },
]

// 清除缓存：清 Service Worker/PWA 资源缓存 + 会话缓存，保留登录态与本地偏好，随后刷新重新拉取资源
async function clearCache() {
  try {
    if ('caches' in globalThis) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
    sessionStorage.clear()
  } catch {
    // 清理失败不阻断提示
  }
  showSuccessToast('清除成功')
  setTimeout(() => location.reload(), 800)
}

// ==================== 子组件 ====================

const IconItem = defineComponent({
  name: 'IconItem',
  props: {
    icon: { type: String, required: true },
    text: { type: String, required: true },
    href: { type: String, default: '' },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const cls = 'btn-reset inline-block text-center relative cursor-pointer w-full active:opacity-70'
    return () => {
      const body = [
        <span class="block">
          <img src={props.icon} alt={props.text} class="w-[0.72rem] h-[0.72rem]" />
        </span>,
        <span class="block py-[0.1rem] text-[0.24rem] text-[#555]">{props.text}</span>,
      ]
      // 有去向：带 href 的链接，点击仍走菜单逻辑（登录判断 / 安卓壳 Custom Tabs）；清除缓存等动作是按钮
      return props.href ? (
        <a
          class={cls}
          href={props.href}
          onClick={(e: MouseEvent) => {
            e.preventDefault()
            emit('click')
          }}
        >
          {body}
        </a>
      ) : (
        <button type="button" class={cls} onClick={() => emit('click')}>
          {body}
        </button>
      )
    }
  },
})

const IconGroup = defineComponent({
  name: 'IconGroup',
  props: {
    title: { type: String, required: true },
    items: { type: Array as () => MenuItem[], required: true },
    hrefOf: { type: Function as unknown as () => (item: MenuItem) => string, required: true },
  },
  emits: ['itemClick'],
  setup(props, { emit }) {
    return () => (
      <div class="mt-[0.3rem] bg-white">
        <div class="text-[0.36rem] px-[0.3rem] py-[0.24rem] font-600">{props.title}</div>
        <div class="text-center">
          <div class="flex flex-wrap">
            {props.items.map((item, i) => (
              <div
                key={item.text}
                class={['w-1/4 py-[0.1rem] pb-[0.2rem]', i > 3 ? 'mt-[0.3rem]' : ''].join(' ')}
              >
                <IconItem
                  icon={item.icon}
                  text={item.text}
                  href={props.hrefOf(item)}
                  onClick={() => emit('itemClick', item)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
})

// ==================== 页面入口 ====================

const MyPage = defineComponent({
  name: 'MyPage',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()

    // 多设备同步：每次进入"我的"页都从服务端拉一次最新昵称/头像
    // 解决在 A 设备改名后，B 设备本地缓存仍是旧昵称的问题
    onMounted(async () => {
      const uid = authStore.user?.id
      if (!authStore.isLoggedIn || !uid) return
      try {
        const res = await getApiV1UserByIdProfile({ path: { id: uid } })
        const data = res.data?.data
        if (!data) return
        const partial: Partial<{ username: string; avatar: string | null }> = {}
        if (data.nickname) partial.username = data.nickname
        if (data.avatar !== undefined) partial.avatar = data.avatar
        if (Object.keys(partial).length) authStore.updateUser(partial)
      } catch {
        // 静默失败，沿用本地缓存
      }
    })

    /** 菜单项的链接地址（与 handleMenuClick 的去向一致）；空串 = 纯动作，渲染成按钮 */
    function menuHref(item: MenuItem): string {
      if (item.action === 'clearCache') return ''
      if (item.authRequired && !authStore.isLoggedIn) return '/login'
      if (item.action === 'userHome') return authStore.user?.id ? `/user/${authStore.user.id}` : ''
      return item.to ?? ''
    }

    function handleMenuClick(item: MenuItem) {
      // 需要登录的入口，未登录时跳转登录页
      if (item.authRequired && !authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      // 特殊入口：我的主页
      if (item.action === 'userHome') {
        const uid = authStore.user?.id
        if (uid) router.push(`/user/${uid}`)
        else showToast('暂无用户 ID')
        return
      }
      // 特殊入口：清除缓存
      if (item.action === 'clearCache') {
        clearCache()
        return
      }
      // 普通跳转
      if (item.to) {
        const bridge = (
          globalThis as unknown as {
            AndroidBridge?: { openCustomTabs?: (url: string) => void }
          }
        ).AndroidBridge
        if (item.nativeOpen && bridge?.openCustomTabs) {
          bridge.openCustomTabs(globalThis.location.origin + item.to)
          return
        }
        router.push(item.to)
      }
    }

    return () => {
      const u = authStore.user
      const loggedIn = authStore.isLoggedIn

      return (
        <div class="pb-[1.2rem] bg-white min-h-screen">
          {/* ===== 顶部绿色区 ===== */}
          <div class="bg-[#07c160] px-[0.3rem] pt-[0.54rem] pb-[0.6rem] relative">
            <div class="h-[1.28rem] relative pl-[1.7rem]">
              {/* 头像 */}
              <div class="absolute left-[0.1rem] w-[1.3rem] h-[1.3rem] rounded-full border-[0.04rem] border-solid border-white overflow-hidden">
                <UserAvatar src={loggedIn ? u?.avatar : ''} alt="avatar" />
              </div>

              {/* 右侧文字区（49208 .user-text padding-top: .2rem） */}
              <div class="text-white pt-[0.2rem] relative">
                {loggedIn ? (
                  <>
                    {/* 名称 */}
                    <div class="text-[0.32rem] font-500 flex items-center text-white">
                      <span class="truncate max-w-[4rem]">
                        {u?.username || u?.phone || '未设置昵称'}
                      </span>
                    </div>

                    {/* 手机号副标题 */}
                    {u?.phone && u?.username && (
                      <div class="text-[0.24rem] text-white/80 pt-[0.1rem]">{u.phone}</div>
                    )}

                    {/* 右上角：设置 */}
                    <div class="absolute right-0 top-[0.24rem]">
                      <RouterLink class="btn-reset" to="/settings">
                        <img
                          src={iconSettings}
                          alt="settings"
                          class="w-[0.32rem] h-[0.32rem] ml-[0.3rem] cursor-pointer inline-block"
                        />
                      </RouterLink>
                    </div>
                  </>
                ) : (
                  <RouterLink
                    class="btn-reset block w-[2.68rem] h-[0.84rem] leading-[0.84rem] bg-white shadow-[0_0.08rem_0.16rem_0_#09a958] rounded-[0.46rem] text-center text-[0.32rem] font-600 text-[#07c160] cursor-pointer"
                    to="/login"
                  >
                    立即登录/注册
                  </RouterLink>
                )}
              </div>
            </div>
          </div>

          {/* ===== 菜单组 ===== */}
          <IconGroup title="我的频道" items={channel} hrefOf={menuHref} onItemClick={handleMenuClick} />
          <IconGroup title="排行榜" items={top} hrefOf={menuHref} onItemClick={handleMenuClick} />
          <IconGroup title="平台信息" items={others} hrefOf={menuHref} onItemClick={handleMenuClick} />
        </div>
      )
    }
  },
})

export default MyPage

// ==================== 旧 UI 参考代码（issue #36 约定：迁移确认前保留） ====================
// 待 /my 新版上线验证无误后，统一在单独 commit 清理下方 _Original / 子组件 / _placeholder

// ==================== 手机号验证码登录 Tab ====================

const PhoneLoginTab = defineComponent({
  name: 'PhoneLoginTab',
  props: {
    captchaId: { type: String, default: '' },
    captchaImage: { type: String, default: '' },
  },
  emits: ['refreshCaptcha'],
  setup(props, { emit }) {
    const router = useRouter()
    const authStore = useAuthStore()
    const message = useMessage()

    const phone = ref('')
    const captchaInput = ref('')
    const code = ref('')
    const loginLoading = ref(false)
    const sendLoading = ref(false)
    const refreshCaptcha = () => emit('refreshCaptcha')
    const { countdown, isCounting, start } = useCountdown()

    async function handleSendCode() {
      if (phone.value.length !== 11) {
        message.warning('请输入正确的手机号')
        return
      }
      sendLoading.value = true
      try {
        const res = await postApiV1SendCode({
          body: {
            channel: 'phone',
            account: phone.value,
            scene: 'sms_login',
            captcha_id: props.captchaId,
            captcha_code: captchaInput.value,
          } as never,
        })
        const data = res.data as { code: number; message: string }
        if (data.code !== 0) {
          message.error(data.message || '验证码发送失败')
          refreshCaptcha()
          return
        }
        message.success('验证码已发送')
        start()
      } catch {
        message.error('验证码发送失败，请稍后重试')
        refreshCaptcha()
      } finally {
        sendLoading.value = false
      }
    }

    async function handleLogin() {
      if (phone.value.length !== 11) {
        message.warning('请输入正确的手机号')
        return
      }
      if (!code.value) {
        message.warning('请输入验证码')
        return
      }
      loginLoading.value = true
      try {
        const res = await postApiV1LoginRegister({
          body: {
            channel: 'phone',
            account: phone.value,
            code: code.value,
            captcha_id: props.captchaId,
            captcha_code: captchaInput.value,
          } as never,
        })
        const {
          code: resCode,
          message: resMsg,
          data,
        } = res.data as {
          code: number
          message: string
          data: {
            token: string
            refresh_token: string
            nickname: string
            user_id?: number
          } | null
        }
        if (resCode !== 0 || !data) {
          message.error(resMsg || '登录失败')
          refreshCaptcha()
          return
        }
        authStore.setAuth(
          data.token,
          { id: data.user_id, username: data.nickname, phone: phone.value },
          data.refresh_token,
        )
        router.push('/')
      } catch {
        message.error('登录失败，请检查验证码是否正确')
        refreshCaptcha()
      } finally {
        loginLoading.value = false
      }
    }

    const inputClass =
      'flex-1 border-none outline-none bg-transparent text-sm text-gray-600 placeholder-gray-400'

    return () => (
      <div class="flex flex-col gap-2 pt-2">
        {/* 手机号 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon icon="mdi:account-outline" width="24" height="24" class="text-green-400 shrink-0" />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">用户名</div>
            <input
              class={inputClass}
              placeholder="输入您手机号码"
              value={phone.value}
              onInput={(e: Event) => {
                const val = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 11)
                phone.value = val
                ;(e.target as HTMLInputElement).value = val
              }}
              type="tel"
              maxlength={11}
            />
          </div>
        </div>

        {/* 图形验证码 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon
            icon="mdi:shield-check-outline"
            width="24"
            height="24"
            class="text-green-400 shrink-0"
          />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">图形验证码</div>
            <input
              class={inputClass}
              placeholder="输入图形验证码"
              value={captchaInput.value}
              onInput={(e: Event) => {
                captchaInput.value = (e.target as HTMLInputElement).value
              }}
            />
          </div>
          <button type="button" class="btn-reset shrink-0" aria-label="刷新验证码" onClick={refreshCaptcha}>
            <img src={props.captchaImage} alt="验证码" class="h-8 cursor-pointer" />
          </button>
        </div>

        {/* 短信验证码 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon
            icon="mdi:message-text-outline"
            width="24"
            height="24"
            class="text-green-400 shrink-0"
          />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">验证码</div>
            <input
              class={inputClass}
              placeholder="输入短信验证码"
              value={code.value}
              onInput={(e: Event) => {
                code.value = (e.target as HTMLInputElement).value
              }}
            />
          </div>
          <button
            type="button"
            class="btn-reset text-sm text-green-500 font-bold cursor-pointer shrink-0 whitespace-nowrap"
            onClick={() => {
              if (!isCounting.value && !sendLoading.value) handleSendCode()
            }}
          >
            {isCounting.value ? `${countdown.value}s` : '获取验证码'}
          </button>
        </div>

        {/* 登录按钮 */}
        <button
          class="w-full h-11 mt-4 bg-green-500 text-white text-base font-bold rounded-full border-none cursor-pointer active:bg-green-600 disabled:opacity-50"
          disabled={loginLoading.value || !phone.value || !code.value}
          onClick={handleLogin}
        >
          {loginLoading.value ? '登录中...' : '登录'}
        </button>
      </div>
    )
  },
})

// ==================== 密码登录 Tab ====================

const PasswordLoginTab = defineComponent({
  name: 'PasswordLoginTab',
  props: {
    captchaId: { type: String, default: '' },
    captchaImage: { type: String, default: '' },
  },
  emits: ['refreshCaptcha'],
  setup(props, { emit }) {
    const router = useRouter()
    const authStore = useAuthStore()
    const message = useMessage()

    const account = ref('')
    const password = ref('')
    const showPassword = ref(false)
    const loginLoading = ref(false)
    const pwdCaptchaInput = ref('')
    const refreshPwdCaptcha = () => emit('refreshCaptcha')

    async function handleLogin() {
      if (!account.value) {
        message.warning('请输入手机号或用户名')
        return
      }
      if (!password.value) {
        message.warning('请输入密码')
        return
      }
      loginLoading.value = true
      try {
        const res = await postApiV1Login({
          body: {
            channel: 'phone',
            account: account.value,
            password: password.value,
            captcha_id: props.captchaId,
            captcha_code: pwdCaptchaInput.value,
          } as never,
        })
        const {
          code: resCode,
          message: resMsg,
          data,
        } = res.data as {
          code: number
          message: string
          data: {
            token: string
            refresh_token: string
            nickname: string
            user_id?: number
          } | null
        }
        if (resCode !== 0 || !data) {
          message.error(resMsg || '登录失败')
          refreshPwdCaptcha()
          return
        }
        authStore.setAuth(
          data.token,
          { id: data.user_id, username: data.nickname, phone: account.value },
          data.refresh_token,
        )
        router.push('/')
      } catch {
        message.error('登录失败，请检查账号或密码是否正确')
        refreshPwdCaptcha()
      } finally {
        loginLoading.value = false
      }
    }

    const inputClass =
      'flex-1 border-none outline-none bg-transparent text-sm text-gray-600 placeholder-gray-400'

    return () => (
      <div class="flex flex-col gap-2 pt-2">
        {/* 用户名 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon icon="mdi:account-outline" width="24" height="24" class="text-green-400 shrink-0" />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">用户名</div>
            <input
              class={inputClass}
              placeholder="输入您手机号码"
              value={account.value}
              onInput={(e: Event) => {
                account.value = (e.target as HTMLInputElement).value
              }}
            />
          </div>
        </div>

        {/* 图形验证码 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon
            icon="mdi:shield-check-outline"
            width="24"
            height="24"
            class="text-green-400 shrink-0"
          />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">图形验证码</div>
            <input
              class={inputClass}
              placeholder="输入图形验证码"
              value={pwdCaptchaInput.value}
              onInput={(e: Event) => {
                pwdCaptchaInput.value = (e.target as HTMLInputElement).value
              }}
            />
          </div>
          <button type="button" class="btn-reset shrink-0" aria-label="刷新验证码" onClick={refreshPwdCaptcha}>
            <img src={props.captchaImage} alt="验证码" class="h-8 cursor-pointer" />
          </button>
        </div>

        {/* 密码 */}
        <div class="flex items-center gap-3 py-3 border-b border-gray-100">
          <Icon
            icon="mdi:shield-check-outline"
            width="24"
            height="24"
            class="text-green-400 shrink-0"
          />
          <div class="flex-1">
            <div class="text-sm font-bold text-gray-800">密码</div>
            <input
              class={inputClass}
              placeholder="输入您的密码锁"
              value={password.value}
              onInput={(e: Event) => {
                password.value = (e.target as HTMLInputElement).value
              }}
              type={showPassword.value ? 'text' : 'password'}
            />
          </div>
          <button
            class="bg-transparent border-none cursor-pointer p-0 shrink-0"
            onClick={() => {
              showPassword.value = !showPassword.value
            }}
          >
            <Icon
              icon={showPassword.value ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
              width="18"
              height="18"
              class="text-gray-400"
            />
          </button>
        </div>

        {/* 登录按钮 */}
        <button
          class="w-full h-11 mt-4 bg-green-500 text-white text-base font-bold rounded-full border-none cursor-pointer active:bg-green-600 disabled:opacity-50"
          disabled={loginLoading.value || !account.value || !password.value}
          onClick={handleLogin}
        >
          {loginLoading.value ? '登录中...' : '登录'}
        </button>
      </div>
    )
  },
})

// ==================== 登录弹层 ====================

const LoginOverlay = defineComponent({
  props: {
    show: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter()
    const activeTab = ref('password')
    const {
      captchaId,
      captchaImage,
      refresh: refreshCaptcha,
    } = useServerCaptcha({
      autoFetch: false,
    })
    let captchaLoaded = false

    return () => {
      // 弹层显示时才加载验证码，且只加载一次
      if (props.show && !captchaLoaded) {
        captchaLoaded = true
        refreshCaptcha()
      }
      if (!props.show) {
        captchaLoaded = false
      }
      return props.show ? (
        <div class="fixed inset-0 z-100 max-w-[480px] mx-auto flex flex-col">
          {/* 绿色顶部 */}
          <div class="bg-green-500 pt-12 pb-16 flex flex-col items-center">
            <div class="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <span class="text-2xl font-bold text-green-500">49</span>
            </div>
            <span class="text-xs text-white/80 mt-2">49TK.COM</span>
          </div>

          {/* 白色卡片 */}
          <div class="flex-1 bg-white -mt-6 rounded-t-3xl px-6 pt-6 overflow-y-auto">
            {/* Tab 栏 + 关闭 */}
            <div class="flex items-center mb-6">
              <button
                type="button"
                class={[
                  'btn-reset text-base mr-6 cursor-pointer pb-1',
                  activeTab.value === 'password'
                    ? 'text-green-500 font-bold border-b-2 border-green-500'
                    : 'text-gray-400',
                ].join(' ')}
                onClick={() => {
                  activeTab.value = 'password'
                }}
              >
                密码登录
              </button>
              <button
                type="button"
                class={[
                  'btn-reset text-base cursor-pointer pb-1',
                  activeTab.value === 'phone'
                    ? 'text-green-500 font-bold border-b-2 border-green-500'
                    : 'text-gray-400',
                ].join(' ')}
                onClick={() => {
                  activeTab.value = 'phone'
                }}
              >
                验证码登录
              </button>
              <button
                class="ml-auto bg-transparent border-none cursor-pointer p-1"
                onClick={() => emit('close')}
              >
                <Icon icon="mdi:close" width="22" height="22" class="text-gray-400" />
              </button>
            </div>

            {/* 表单内容 */}
            {activeTab.value === 'password' ? (
              <PasswordLoginTab
                captchaId={captchaId.value}
                captchaImage={captchaImage.value}
                onRefreshCaptcha={refreshCaptcha}
              />
            ) : (
              <PhoneLoginTab
                captchaId={captchaId.value}
                captchaImage={captchaImage.value}
                onRefreshCaptcha={refreshCaptcha}
              />
            )}

            {/* 底部链接 */}
            <div class="flex justify-between pt-4 pb-8 text-sm">
              <button
                class="bg-transparent border-none cursor-pointer text-sm text-green-500 p-0"
                onClick={() => {
                  emit('close')
                  router.push('/register')
                }}
              >
                立即注册
              </button>
              <button
                class="bg-transparent border-none cursor-pointer text-sm text-gray-400 p-0"
                onClick={() => {
                  emit('close')
                  router.push('/forgot-password')
                }}
              >
                忘记密码
              </button>
            </div>
          </div>
        </div>
      ) : null
    }
  },
})

// ==================== 未登录：首页视图 ====================

const MyLoginView = defineComponent({
  name: 'MyLoginView',
  setup() {
    const message = useMessage()
    const showLogin = ref(false)

    function requireLogin() {
      message.warning('请先登录')
    }

    return () => (
      <div class="max-w-[480px] mx-auto min-h-screen bg-[#f5f5f5] flex flex-col pb-[calc(52px+env(safe-area-inset-bottom))]">
        {/* 绿色头部 */}
        <div class="bg-green-500 pt-6 pb-8 px-4 flex items-center gap-3">
          <div class="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
            <span class="text-lg font-bold text-green-500">49</span>
          </div>
          <button
            class="bg-white text-green-600 font-bold text-sm px-5 py-2 rounded-full border-none cursor-pointer"
            onClick={() => {
              showLogin.value = true
            }}
          >
            立即登录/注册
          </button>
        </div>

        {/* 我的频道 */}
        <div class="mx-3 mt-3 bg-white rounded-lg p-4 mb-4">
          <div class="text-sm font-bold text-gray-800 mb-3">我的频道</div>
          <div class="grid grid-cols-4 gap-y-4 text-center">
            <ChannelItem
              icon="mdi:account-circle-outline"
              label="我的主页"
              color="text-blue-500"
              onClick={requireLogin}
            />
            <ChannelItem
              icon="mdi:star-outline"
              label="我的收藏"
              color="text-yellow-500"
              onClick={requireLogin}
            />
            <ChannelItem
              icon="mdi:comment-text-outline"
              label="我的评论"
              color="text-green-500"
              onClick={requireLogin}
            />
            <ChannelItem
              icon="mdi:heart-outline"
              label="我的关注"
              color="text-red-400"
              onClick={requireLogin}
            />
            <ChannelItem
              icon="mdi:account-group-outline"
              label="我的粉丝"
              color="text-blue-500"
              onClick={requireLogin}
            />
            <ChannelItem
              icon="mdi:thumb-up-outline"
              label="我的点赞"
              color="text-red-500"
              onClick={requireLogin}
            />
          </div>
        </div>

        {/* 登录弹层 */}
        <LoginOverlay
          show={showLogin.value}
          onClose={() => {
            showLogin.value = false
          }}
        />
      </div>
    )
  },
})

// ==================== 频道入口组件 ====================

const ChannelItem = defineComponent({
  props: {
    icon: { type: String, required: true },
    label: { type: String, required: true },
    color: { type: String, default: 'text-gray-600' },
  },
  emits: ['click'],
  setup(props, { emit }) {
    return () => (
      <button
        type="button"
        class="btn-reset flex flex-col items-center gap-1 cursor-pointer"
        onClick={() => emit('click')}
      >
        <Icon icon={props.icon} width="28" height="28" class={props.color} />
        <span class="text-xs text-gray-600">{props.label}</span>
      </button>
    )
  },
})

// ==================== 已登录：用户中心视图 ====================

const MyProfileView = defineComponent({
  name: 'MyProfileView',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const userStore = useUserStore()
    const message = useMessage()

    const showNicknameModal = ref(false)
    const nicknameInput = ref('')
    const nicknameSaving = ref(false)

    function openNicknameModal() {
      nicknameInput.value = authStore.user?.username || ''
      showNicknameModal.value = true
    }

    async function submitNickname() {
      const val = nicknameInput.value.trim()
      if (val.length < 2 || val.length > 10) {
        message.warning('昵称长度为 2~10 个字符')
        return
      }
      nicknameSaving.value = true
      try {
        const result = await userStore.updateNickname(val)
        if (result.code === 0) {
          message.success('昵称修改成功')
          showNicknameModal.value = false
        } else {
          message.error(result.message || '修改失败')
        }
      } catch {
        message.error('修改失败')
      } finally {
        nicknameSaving.value = false
      }
    }

    return () => {
      const u = authStore.user
      return (
        <div class="max-w-[480px] mx-auto min-h-screen bg-[#f5f5f5] flex flex-col pb-[calc(52px+env(safe-area-inset-bottom))]">
          {/* 绿色头部卡片 */}
          <div class="bg-green-500 pt-6 pb-8 px-4 text-white">
            <div class="flex items-center gap-3">
              <UserAvatar size={56} src={u?.avatar} />
              <div class="flex-1 min-w-0">
                <span class="text-base font-bold truncate">{u?.username || u?.phone}</span>
              </div>
              {/* 修改昵称 */}
              <button
                class="bg-transparent border-none cursor-pointer p-1"
                onClick={openNicknameModal}
              >
                <Icon icon="mdi:pencil-outline" width="22" height="22" class="text-white" />
              </button>
              {/* 设置个人信息 */}
              <button
                class="bg-transparent border-none cursor-pointer p-1"
                onClick={() => router.push('/settings')}
              >
                <Icon icon="mdi:cog-outline" width="22" height="22" class="text-white" />
              </button>
            </div>
          </div>

          {/* 我的频道 */}
          <div class="mx-3 mt-3 bg-white rounded-lg p-4 mb-4">
            <div class="text-sm font-bold text-gray-800 mb-3">我的频道</div>
            <div class="grid grid-cols-4 gap-y-4 text-center">
              <ChannelItem
                icon="mdi:account-circle-outline"
                label="我的主页"
                color="text-blue-500"
                onClick={() => {
                  if (u?.id) {
                    router.push(`/user/${u.id}`)
                  } else {
                    message.info('暂无用户ID，请重新登录')
                  }
                }}
              />
              <ChannelItem
                icon="mdi:star-outline"
                label="我的收藏"
                color="text-yellow-500"
                onClick={() => router.push('/my-collections')}
              />
              <ChannelItem
                icon="mdi:comment-text-outline"
                label="我的评论"
                color="text-green-500"
                onClick={() => router.push('/my-comments')}
              />
              <ChannelItem
                icon="mdi:heart-outline"
                label="我的关注"
                color="text-red-400"
                onClick={() => router.push('/my-follows')}
              />
              <ChannelItem
                icon="mdi:account-group-outline"
                label="我的粉丝"
                color="text-blue-500"
                onClick={() => router.push('/my-fans')}
              />
              <ChannelItem
                icon="mdi:thumb-up-outline"
                label="我的点赞"
                color="text-red-500"
                onClick={() => router.push('/my-likes')}
              />
            </div>
          </div>

          {/* 修改昵称弹窗 */}
          <NModal
            show={showNicknameModal.value}
            onUpdateShow={(v) => {
              showNicknameModal.value = v
            }}
            preset="dialog"
            title="修改昵称"
            positiveText="确定"
            negativeText="取消"
            loading={nicknameSaving.value}
            onPositiveClick={submitNickname}
          >
            <NInput
              value={nicknameInput.value}
              onUpdateValue={(v) => {
                nicknameInput.value = v
              }}
              placeholder="请输入昵称（2~10个字符）"
              maxlength={10}
            />
          </NModal>
        </div>
      )
    }
  },
})

// ==================== 页面入口 ====================

const _Original = defineComponent({
  name: 'MyPage',
  setup() {
    const authStore = useAuthStore()
    return () => (authStore.isLoggedIn ? <MyProfileView /> : <MyLoginView />)
  },
})
const _placeholder = defineComponent({ setup: () => () => <div /> })

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _keepRef = [_Original, _placeholder]
